import { motion } from "framer-motion";
import type { Lang } from "../lib/i18n";
import { pick } from "../lib/i18n";
import type { UserProfile } from "../lib/auth";
import { stepVariants, respectMotion } from "../lib/motion";
import { pickWelcomeMessage } from "../data/welcomeMessages";
import { firstDayPriorities } from "../data/firstDay";
import { OrgChart } from "../components/OrgChart";
import { HR_EMAIL, IT_EMAIL } from "../lib/contacts";

const EMPTY_PROFILE: UserProfile = {
  name: "",
  email: "",
  role: "",
  photo: null,
  phone: "",
  manager: "",
  managerEmail: "",
  managerPerson: null,
  managerChain: [],
  peers: [],
  peerPersons: [],
};

interface ProfileScreenProps {
  lang: Lang;
  profile: UserProfile | null;
  loading: boolean;
  onNext: () => void;
}

/** Step 2 — Welcome + Microsoft Graph profile, org chart, and day-one priorities. */
export function ProfileScreen({ lang, profile, loading, onNext }: ProfileScreenProps) {
  const t = (ar: string, en: string) => pick(lang, ar, en);

  if (loading) {
    return (
      <div className="kd-step-container" dir={lang === "ar" ? "rtl" : "ltr"}>
        <div className="glass-panel" role="status" aria-live="polite">
          <span className="kd-sr-only">{t("جارٍ استرجاع بياناتك...", "Loading your profile...")}</span>
          <div className="kd-skeleton-card">
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div className="kd-skeleton kd-skeleton-circle" style={{ width: 80, height: 80 }} />
              <div style={{ flex: 1 }}>
                <div className="kd-skeleton kd-skeleton-line" style={{ width: "60%", height: 18 }} />
                <div className="kd-skeleton kd-skeleton-line" style={{ width: "40%" }} />
                <div className="kd-skeleton kd-skeleton-line" style={{ width: "50%" }} />
              </div>
            </div>
            <div style={{ marginTop: 24 }}>
              <div className="kd-skeleton kd-skeleton-line" style={{ width: "30%", height: 16 }} />
              <div className="kd-skeleton kd-skeleton-line" style={{ width: "90%" }} />
              <div className="kd-skeleton kd-skeleton-line" style={{ width: "75%" }} />
              <div className="kd-skeleton kd-skeleton-line" style={{ width: "85%" }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const p = profile || EMPTY_PROFILE;
  const firstName = p.name ? p.name.split(" ")[0] || p.name : "";
  const welcome = p.name ? pickWelcomeMessage(p.name) : null;

  return (
    <motion.div
      className="kd-step-container"
      dir={lang === "ar" ? "rtl" : "ltr"}
      variants={respectMotion(stepVariants)}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div className="glass-panel step-profile">
        <div className="kd-step-header">
          <h1>{t("أهلاً بك في كادن", "Welcome to Kaden")}</h1>
        </div>

        {p.name && welcome && (
          <div className="kd-welcome-banner">
            <div className="kd-welcome-body">
              <h2 className="kd-welcome-title">{t(`أهلاً بك يا ${firstName} في كادن للاستثمار`, `Welcome ${firstName} to Kaden Investment`)}</h2>
              <p className="kd-welcome-msg">{lang === "ar" ? welcome.ar : welcome.en}</p>
            </div>
          </div>
        )}

        <div className="kd-profile-card kd-profile-card-centered">
          <div className="kd-profile-photo-wrap">
            {p.photo ? <img src={p.photo} alt={p.name} className="kd-profile-photo" /> : <div className="kd-profile-avatar">{(p.name || "?")[0]}</div>}
          </div>
          <div className="kd-profile-info">
            <h2 className="kd-profile-name">{p.name || t("غير متوفر", "Not available")}</h2>
            {p.role && <p className="kd-profile-role">{p.role}</p>}
            {p.phone && (
              <p className="kd-profile-detail">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.7 19.79 19.79 0 0 1 1.61 5.1 2 2 0 0 1 3.58 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.91a16 16 0 0 0 6.06 6.06l1.97-1.97a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                <span>{p.phone}</span>
              </p>
            )}
            {p.manager && (
              <p className="kd-profile-detail">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span>
                  {t("المدير المباشر:", "Direct Manager:")} {p.manager}
                </span>
              </p>
            )}
          </div>
        </div>

        <OrgChart lang={lang} profile={p} />

        <div className="kd-first-day-section">
          <h3 className="kd-first-day-title">{t("أولويات اليوم الأول", "Day-One Priorities")}</h3>
          <p className="kd-first-day-subtitle">
            {t("تأكد من إتمام هذه الخطوات الأساسية في أقرب وقت:", "Make sure to complete these essential steps as soon as possible:")}
          </p>
          <div className="kd-first-day-grid">
            {firstDayPriorities.map((item, i) => {
              const c = lang === "ar" ? item.ar : item.en;
              return (
                <div className="kd-first-day-card" key={i}>
                  <img src={item.icon} alt={c.title} className="kd-first-day-icon-img" />
                  <div>
                    <h4>{c.title}</h4>
                    <p>{c.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <button className="kd-btn-primary kd-btn-centered" onClick={onNext}>
          <span>{t("إبدأ الرحلة", "Start the journey")}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points={lang === "ar" ? "15 18 9 12 15 6" : "9 18 15 12 9 6"} />
          </svg>
        </button>

        <p className="kd-profile-footnote">
          {t("لاحظت أي معلومة غير صحيحة؟ راسل ", "Spotted something off? Drop a note to ")}
          <a href={`mailto:${HR_EMAIL}?subject=${encodeURIComponent("Profile data correction")}`}>{t("الموارد البشرية", "HR")}</a>
          {t(" أو ", " or ")}
          <a href={`mailto:${IT_EMAIL}?subject=${encodeURIComponent("Profile data correction")}`}>{t("تقنية المعلومات", "IT")}</a>
          {t(" وسنحدّثها لك.", " and we'll update it for you.")}
        </p>
      </div>
    </motion.div>
  );
}
